import {
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  useDisclosure,
} from "@chakra-ui/react";
import { signOut } from "firebase/auth";
import { useRouter } from "next/router";
import { ReactElement, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useShallow } from "zustand/react/shallow";

import { T_KEY } from "@/src/constants/translationsKey";

import { auth } from "../../../../../firebaseClient";
import { dialogMessage } from "../../../../constants/dialogMessage";
import { TEXT } from "../../../../constants/user-management/consts";
import useAuth from "../../../../hooks/user-management/useAuth";
import useUserAuthStore, {
  clearLoginFlag,
} from "../../../../store/client/user-management/auth";
import { LOCALES_SHORT } from "../../../../utils/types";
import CustomAlertDialog from "../CustomAlertDialog/CustomAlertDialog";

import styles from "./CustomMenu.module.scss";

interface CustomMenuProps {
  icon: ReactElement;
}

export default function CustomUserMenu({ icon }: CustomMenuProps) {
  const { t } = useTranslation(T_KEY);
  const router = useRouter();
  const { locale } = useRouter();
  const transformLocale =
    locale === LOCALES_SHORT.KO ? LOCALES_SHORT.KO : LOCALES_SHORT.EN;
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { logOut, loginMethod, error, setError } = useAuth(true);
  const [localError, setLocalError] = useState(false);
  const [isMenuOpen, setMenuOpen] = useState(false);
  const isPhoneLoginUser = loginMethod === "phone";
  const isEmailLoginUser = loginMethod === "email";

  // 에러 관련 팝업을 닫고 에러를 초기화한다.
  const closeErrorDialog = () => {
    onClose();
    setError(null);
    setLocalError(false);
  };

  const handleLogout = async () => {
    try {
      const { error: err } = await logOut(transformLocale);

      if (err) {
        setError(err);
        throw new Error(err.message);
      }
    } catch {
      setLocalError(true);
    }
  };

  const requesetDeleteAccount = () => {
    // 전화번호 또는 이메일 로그인 유저인 경우 비밀번호 확인 페이지로 이동
    if (isPhoneLoginUser || isEmailLoginUser) {
      router.push("/delete-account/confirm-password");
      return;
    }

    // 이메일, 구글, 애플 로그인 유저인 경우 회원 탈퇴 다이얼로그 띄우기
    onOpen();
  };

  const { deleteUserAccount } = useUserAuthStore(
    useShallow(state => ({
      deleteUserAccount: state.deleteUserAccount,
    }))
  );
  const handleDeleteAccount = async () => {
    try {
      const { error: err } = await deleteUserAccount(transformLocale);
      if (err) {
        setError(err);
        throw new Error(err.message);
      }

      // !: 에러가 발생하지 않은 경우 정상 진행
      await signOut(auth);
      clearLoginFlag(); // 로그아웃 시 세션에서 로그인 플래그 제거
    } catch {
      setLocalError(true);
    }
  };

  // 에러 다이얼로그 띄우기
  useEffect(() => {
    if (localError && error) {
      onOpen();
    }
  }, [error, localError, onOpen]);

  return (
    <div className={styles.menu}>
      <Menu
        isOpen={isMenuOpen}
        onClose={() => setMenuOpen(false)}
        onOpen={() => setMenuOpen(true)}
      >
        <MenuButton
          as={IconButton}
          aria-label="Options"
          icon={icon}
          borderRadius="99999"
          borderWidth="0px"
          px={0}
          py={0}
          bg="transparent"
          _hover={{ bg: "transparent" }}
          _expanded={{ bg: "transparent" }}
        />

        <MenuList w="auto" zIndex="10000">
          <MenuItem color="rgba(0, 0, 0, 1)" onClick={handleLogout}>
            {t(TEXT.logout)}
          </MenuItem>
          <MenuItem color="rgba(0, 0, 0, 1)" onClick={requesetDeleteAccount}>
            {t(TEXT.deleteUserAccount)}
          </MenuItem>
        </MenuList>
      </Menu>

      <CustomAlertDialog
        isOpen={isOpen}
        onClose={closeErrorDialog}
        title={dialogMessage.withdrawalRequest.title}
        body={dialogMessage.withdrawalRequest.message}
        alertDialogFooterProps={{
          display: "flex",
          gap: "12px",
        }}
        footerButtons={[
          {
            text: dialogMessage.cancel,
            w: "50%",
            onClick: onClose,
            colorScheme: "gray",
            isCancelButton: true,
          },
          {
            text: dialogMessage.withdrawalRequest.buttons[0].confirm,
            w: "50%",
            onClick: handleDeleteAccount,
            colorScheme: "red",
          },
        ]}
      />
    </div>
  );
}
